import React, { useEffect, useState } from 'react'
import dayjs from 'dayjs'
import { FormProvider, useForm } from 'react-hook-form'
import { Button, Popconfirm } from 'antd'
import { useLocation } from 'react-router-dom'
import { yupResolver } from '@hookform/resolvers/yup'
import Schema from './schema'
import { FormInput, OutlineDeleteButton } from '../../../../components'
import { FormTextArea, FormTreeSelect } from '../../../../components/form'
import { Wrapper } from './styled'
import { Assignee } from '../form-assignee'
import { TASK_CATEGORY_KEY, TASK_CATEGORY_VALUE, STATUS, STATUS_KEY, STATUS_VALUE, TASK_TYPE_NAME } from '../../constants'
import { DueDateTime } from '../form-due-date-time'
import { Status } from '../form-status'
import { useHistories, useProfile, useTaskManagement } from '../../../../hooks'
import { ROUTES_NAME } from '../../../../constants'
import { Requester } from '../form-requester'
import useQuery from '../../../../hooks/useQuery'

const currentTime = dayjs().minute(0).second(0).millisecond(0)

export const DEFAULT_VALUE = {
  title: '',
  dueDate: currentTime,
  dueTime: currentTime,
  targetValue: 100,
  valueUnit: '%',
  achievementValue: null,
  memo: '',
  status: STATUS.NOT_STARTED
}

const getTaskCategoryOptions = ({ t, taskCategories }) => Object.entries(taskCategories).reduce((options, [key, items]) => {
  if (key === TASK_CATEGORY_KEY.MY_TASK) {
    return options.concat(
      items.map(({ taskTypeId, taskTypeName }) => ({
        title: t('task.my_task'),
        value: taskTypeId,
        taskTypeName
      }))
    )
  }

  const title = key === TASK_CATEGORY_KEY.DEPARTMENT ? t('task.department') : t('task.attribute')
  options.push({
    title,
    value: title,
    disabled: true,
    children: items.map(({ name, id, taskTypeName, children }) => ({
      title: name,
      value: id,
      taskTypeName,
      children: children ? getTaskCategoryOptions({ t, taskCategories: children }) : []
    }))
  })
  return options
}, [])

export const getDueDateString = (dueDate, dueTime) => {
  const date = dueDate.format('YYYY-MM-DD')
  const time = dueTime.second(0).millisecond(0).format('HH:mm:ss')
  return `${date}T${time}Z`
}

export const getMyStatusName = (assignees, userId) => {
  const myself = assignees.find(({ id }) => id === userId)
  return STATUS_KEY[myself?.taskStatus] || STATUS.NOT_STARTED
}

const findTaskCategoryById = (options, targetId) => options.map((option) => {
  if (option.value === Number(targetId)) {
    return option
  }
  if (option.children && option.children.length > 0) {
    const childOption = option.children.find((child) => child.value === Number(targetId))
    return childOption || null
  }
  return null
})
  .find((option) => option !== null)

export const TaskForm = ({ t }) => {
  const { profile: { userId } } = useProfile()
  const query = useQuery()
  const taskTypeId = query.get('taskTypeId') || TASK_CATEGORY_VALUE.MY_TASK
  const taskCategoryId = query.get('taskCategoryId') || userId
  const history = useHistories()
  const location = useLocation()
  const task = location.state ? location.state.task : null
  const { taskCategories, registerNewTaskAction, isCreateTask, isEditTask, deleteTaskAction, updateTaskAction } = useTaskManagement()
  const taskCategoryOptions = getTaskCategoryOptions({ t, taskCategories })
  const form = useForm({
    resolver: yupResolver(Schema(t)),
    defaultValues: {
      ...DEFAULT_VALUE,
      taskCategory: {
        title: t('task.my_task'),
        value: TASK_CATEGORY_VALUE.MY_TASK,
        taskTypeName: TASK_TYPE_NAME.MY_TASK
      }
    }
  })
  const { handleSubmit, reset, watch } = form
  const [taskCategoryWatch] = watch(['taskCategory'])
  const [registeredAssignees, setRegisteredAssignees] = useState([])
  const [registeredRequester, setRegisteredRequester] = useState(null)
  const [selectedStatus, setSelectedStatus] = useState(STATUS.NOT_STARTED)
  // タスクの依頼者のみが変更可能
  const isEditable = isCreateTask || (isEditTask && task?.isRequesterMe)

  useEffect(() => {
    if (task) {
      // タスク編集時
      const { taskType, dueDateStr, assignees, requesterId, requesterName, isRequesterMe } = task
      const taskCategory = taskType.taskTypeId === TASK_CATEGORY_VALUE.MY_TASK ? {
        title: t('task.my_task'),
        value: TASK_CATEGORY_VALUE.MY_TASK,
        taskTypeName: TASK_TYPE_NAME.MY_TASK
      } : {
        title: taskType.name,
        value: taskType.id,
        taskTypeName: taskType.taskTypeName
      }

      const dueDate = dueDateStr ? dayjs(dueDateStr) : null
      reset({ ...task, taskCategory, dueDate, dueTime: dueDate })
      setRegisteredAssignees(assignees)
      setRegisteredRequester({ id: requesterId, name: requesterName })
      const myStatus = isRequesterMe ? STATUS_KEY[task.status] : getMyStatusName(assignees, userId)
      setSelectedStatus(myStatus)
    } else {
      // タスク新規作成時
      // 左サイドバーでタスクカテゴリーを選択し、新規作成ボタンを押すことで
      // taskCategoryId に一致するタスクカテゴリーを探して、登録先に設定する
      // https://soft.backlog.jp/view/NSK_GC-388
      const taskCategory = findTaskCategoryById(taskCategoryOptions, taskCategoryId)

      if (taskCategory) {
        reset({ ...DEFAULT_VALUE,
          taskCategory: {
            title: taskCategory.title,
            value: taskCategory.value,
            taskTypeName: taskCategory.taskTypeName
          }
        })
      } else {
        reset({
          ...DEFAULT_VALUE,
          taskCategory: {
            title: t('task.my_task'),
            value: TASK_CATEGORY_VALUE.MY_TASK,
            taskTypeName: TASK_TYPE_NAME.MY_TASK
          }
        })
      }
    }
  }, [task, reset, t, taskCategoryOptions.length, userId])

  const handleSelectStatus = (e) => {
    const { value } = e.target
    setSelectedStatus(value)
  }

  const handleOK = (formData) => {
    const { achievementValue, dueDate, dueTime, memo, targetValue, title, valueUnit } = formData

    const { taskTypeName } = taskCategoryWatch
    const isMyTask = taskTypeName === TASK_TYPE_NAME.MY_TASK

    const myTask = { type: TASK_CATEGORY_VALUE.MY_TASK, referenceId: userId }
    const visibility = isMyTask
      ? [myTask]
      : [myTask, { type: TASK_CATEGORY_VALUE[taskTypeName], referenceId: taskCategoryWatch.value }]

    const taskData = {
      achievementValue: achievementValue || 0,
      assigneeIds: registeredAssignees.length > 0 ? registeredAssignees.map(({ id }) => id) : [userId],
      dueDate: getDueDateString(dueDate, dueTime),
      memo,
      requesterId: registeredRequester?.id || userId,
      status: STATUS_VALUE[selectedStatus],
      targetValue: targetValue || DEFAULT_VALUE.targetValue,
      title,
      valueUnit,
      visibility
    }

    const payload = {
      ...taskData,
      callback: () => {
        if (task?.taskVisibilityType === TASK_CATEGORY_VALUE.MY_TASK) {
          // 編集画面で、マイタスクの場合は、マイタスクの一覧に戻る
          history.push(`${ROUTES_NAME.TASK}?taskTypeId=${TASK_CATEGORY_VALUE.MY_TASK}&taskCategoryId=${userId}`)
        } else {
          history.push(`${ROUTES_NAME.TASK}?taskTypeId=${TASK_CATEGORY_VALUE[taskTypeName]}&taskCategoryId=${taskCategoryWatch.value || userId}`)
        }
      }
    }

    if (task) {
      updateTaskAction({ ...payload, id: task.id })
    } else {
      registerNewTaskAction(payload)
    }
  }

  const handleCancel = () => {
    reset(DEFAULT_VALUE)
    setRegisteredAssignees([])
    setSelectedStatus(STATUS.NOT_STARTED)
    history.push(`${ROUTES_NAME.TASK}?taskTypeId=${taskTypeId}&taskCategoryId=${taskCategoryId}`)
  }

  const handleDeleteTask = () => {
    const { id } = task
    const callback = () => {
      history.push(`${ROUTES_NAME.TASK}?taskTypeId=${TASK_CATEGORY_VALUE[task.taskVisibilityTypeName]}&taskCategoryId=${taskCategoryWatch.value || userId}`)
    }
    deleteTaskAction({ id, callback })
  }

  return (
    <Wrapper>
      <FormProvider {...form}>
        <form>
          {/* 登録先 */}
          {isEditable ? (
            <FormTreeSelect
              t={t}
              name="taskCategory"
              label={t('task.assigned_to')}
              isRequired
              treeData={taskCategoryOptions}
              disabled={!isEditable}
              wrapperProps={{
                colon: false,
                labelAlign: 'left'
              }}
            />
          ) : (
            <>
              <input type="hidden" name="taskCategory" value={taskCategoryWatch.value} />
              <FormInput
                t={t}
                name=""
                label={t('task.assigned_to')}
                value={task?.taskType.name}
                isRequired
                disabled
                wrapperProps={{
                  colon: false,
                  labelAlign: 'left'
                }}
              />
            </>
          )}

          {/* タイトル */}
          <FormInput
            t={t}
            name="title"
            label={t('task.title')}
            isRequired
            disabled={!isEditable}
            wrapperProps={{
              colon: false,
              labelAlign: 'left'
            }}
          />

          {/* 期日・時間 */}
          <DueDateTime
            t={t}
            isRequired
            disabled={!isEditable}
            timeProps={{
              allowClear: false
            }}
          />

          {/* 実施者 */}
          <Assignee
            t={t}
            taskCategory={taskCategoryWatch}
            registeredAssignees={registeredAssignees}
            setRegisteredAssignees={setRegisteredAssignees}
            disabled={!isEditable}
          />

          {/* 依頼者 */}
          <Requester
            t={t}
            isCreateTask={isCreateTask}
            taskCategory={taskCategoryWatch}
            registeredRequester={registeredRequester}
            setRegisteredRequester={setRegisteredRequester}
            disabled={!isEditable}
          />

          {/* 目標・実績 */}
          <div className="target-achievement">
            <FormInput
              t={t}
              name="targetValue"
              label={t('task.target')}
              wrapperProps={{
                colon: false,
                labelAlign: 'left'
              }}
              style={{ textAlign: 'right' }}
            />

            <FormInput
              t={t}
              name="valueUnit"
              label={t('task.unit')}
              wrapperProps={{
                colon: false,
                labelAlign: 'left'
              }}
              style={{ textAlign: 'center' }}
            />

            <FormInput
              t={t}
              name="achievementValue"
              label={t('task.achievement')}
              wrapperProps={{
                colon: false,
                labelAlign: 'left'
              }}
              style={{ textAlign: 'right' }}
            />
          </div>

          {/* メモ */}
          <FormTextArea
            t={t}
            name="memo"
            label={t('task.memo')}
            disabled={!isEditable}
            wrapperProps={{
              colon: false,
              labelAlign: 'left'
            }}
          />

          {/* ステータス */}
          <Status t={t} selectedStatus={selectedStatus} handleSelectStatus={handleSelectStatus} />

          <div className="form-action-group">
            {!task?.isNotRequesterAndNotAssignee && (
            <Button
              className="btn-post"
              type="primary"
              htmlType="submit"
              onClick={handleSubmit(handleOK)}
            >
              {t('task.save')}
            </Button>
            )}

            <Button
              className="btn-cancel"
              htmlType="button"
              onClick={handleCancel}
            >
              {t('task.cancel')}
            </Button>

            {/* タスクを削除できるのは依頼人のみ */}
            {isEditTask && task?.isRequesterMe && (
            <Popconfirm
              onConfirm={handleDeleteTask}
              cancelText={t('common.cancel')}
              title={t('task.delete_confirm')}
            >
              <OutlineDeleteButton title={t('task.delete')} color="error" style={{ width: 130, height: 40, borderRadius: 6 }} />
            </Popconfirm>
            )}
          </div>
        </form>
      </FormProvider>
    </Wrapper>
  )
}
